import { PluginState } from "@intelligems/ig-types";
import {
  ProxyStateController,
  ProxyStateEventMap,
} from "@src/components/ReverseProxy/ProxyStateController";
import { ReverseProxyURLOptions } from "@src/components/ReverseProxy/ReverseProxyController";

export type PluginStateEventMap = ProxyStateEventMap & {
  version: string | undefined;
};

export type PluginStateURLOptions = ReverseProxyURLOptions;

export class PluginStateController<
  Events extends PluginStateEventMap = PluginStateEventMap,
  URLOptions extends PluginStateURLOptions = PluginStateURLOptions,
> extends ProxyStateController<Events, URLOptions> {
  private version: string | undefined;
  private pluginState: PluginState | undefined;

  constructor() {
    super();
    this.timeoutDuration = 15_000;
  }

  override update() {
    super.update();
    const version = this.getVersion();
    if (version !== this.version) {
      this.version = version;
      this.emit("version", version);
    }
  }

  override onPluginState(pluginState: PluginState) {
    super.onPluginState(pluginState);
    this.pluginState = pluginState;
    this.update();
  }

  override resetState() {
    super.resetState();
    this.pluginState = undefined;
  }

  override getLoadState() {
    if (this.errorFired || this.targetUrl === null) {
      return { type: "error" } as const;
    }
    if (this.pluginState || this.isAuthPageLoaded) {
      return { type: "ready" } as const;
    }
    if (this.timerFired) {
      if (this.commitPending) {
        return { type: "ready" } as const;
      }
      if (!this.proxyState) {
        return { type: "error", subtype: "proxy" } as const;
      }
      if (!this.pluginState) {
        return { type: "error", subtype: "plugin" } as const;
      }
    }
    return undefined;
  }

  protected getVersion() {
    return this.pluginState ? (this.pluginState.version ?? null) : undefined;
  }
}
